// Phase 1 — System Health Card (Super-Admin only).
// Aggregated platform overview: tenants, users, security, storage, jobs, audit.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, Building2, Users, ShieldAlert, Database,
  RefreshCw, AlertTriangle, CheckCircle2, Clock, Loader2,
} from "lucide-react";

function fmtNum(n) { return n == null ? "—" : Number(n).toLocaleString("de-AT"); }

export default function SystemHealthCard() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const [{ data: ov }, { data: rec }] = await Promise.all([
        api.get("/admin/health/overview"),
        api.get("/admin/health/recent-audit?limit=10"),
      ]);
      setData(ov);
      setAudit(Array.isArray(rec) ? rec : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "System-Health nicht ladbar");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 flex items-center gap-2 text-zinc-400 text-sm" data-testid="system-health-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade System-Health…
      </div>
    );
  }
  if (!data) return null;

  const failedLogins = data.security?.failed_logins_24h || 0;
  const auditFails = data.activity?.payroll_export_failures_24h || 0;
  const schedulerOk = !!data.jobs?.scheduler_running;
  const overallOk = failedLogins < 20 && auditFails === 0 && schedulerOk;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="system-health-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-400" />
          <h3 className="font-display font-semibold text-zinc-100">System-Health</h3>
        </div>
        <button onClick={load} data-testid="health-refresh"
                className="p-1.5 rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className={`border rounded p-3 mb-4 flex items-start gap-2 ${overallOk
        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
        : "bg-amber-500/10 border-amber-500/30 text-amber-300"}`}
        data-testid="health-banner">
        {overallOk ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
        <div className="text-xs">
          {overallOk
            ? "Alle Systeme nominal. Scheduler läuft, keine Auffälligkeiten in den letzten 24h."
            : "Achtung — Auffälligkeiten erkannt. Bitte unten prüfen."}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI icon={Building2} label="Mandanten (aktiv)"
             value={`${fmtNum(data.tenants?.active)} / ${fmtNum(data.tenants?.total)}`}
             accent="emerald" testid="kpi-tenants"/>
        <KPI icon={Users} label="User aktiv (7T)"
             value={`${fmtNum(data.users?.active_7d)} / ${fmtNum(data.users?.total)}`}
             accent="blue" testid="kpi-users"/>
        <KPI icon={ShieldAlert} label="Failed Logins 24h"
             value={fmtNum(failedLogins)}
             accent={failedLogins > 20 ? "rose" : failedLogins > 5 ? "amber" : "zinc"}
             testid="kpi-failed-logins"/>
        <KPI icon={Database} label="Dokumente (DB)"
             value={fmtNum(data.storage?.total_docs)}
             accent="zinc" testid="kpi-storage"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="storage-breakdown">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Top Collections</div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {(data.storage?.collections || []).map((c) => (
              <div key={c.name} className="flex justify-between text-xs px-1 py-0.5 rounded hover:bg-zinc-800/50">
                <span className="font-mono text-zinc-300 truncate">{c.name}</span>
                <span className="text-zinc-500 font-mono">{fmtNum(c.doc_count)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="jobs-status">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            Background-Jobs
            <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] ${schedulerOk
              ? "bg-emerald-500/20 text-emerald-300"
              : "bg-rose-500/20 text-rose-300"}`}>
              {schedulerOk ? "RUNNING" : "STOPPED"}
            </span>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {(data.jobs?.jobs || []).map((j) => (
              <div key={j.id} className="text-xs px-1 py-0.5">
                <div className="font-mono text-zinc-200">{j.id}</div>
                <div className="text-[10px] text-zinc-500">
                  Nächster Lauf: {j.next_run_time
                    ? new Date(j.next_run_time).toLocaleString("de-AT")
                    : "—"}
                </div>
              </div>
            ))}
            {(!data.jobs?.jobs || data.jobs.jobs.length === 0) && (
              <div className="text-xs text-zinc-500">Keine geplanten Jobs.</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="recent-audit">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-2">
          <Clock className="w-3 h-3" /> Letzte Audit-Events
        </div>
        <div className="space-y-1 max-h-56 overflow-y-auto text-xs">
          {audit.length === 0 ? (
            <div className="text-zinc-500 px-1">Keine Einträge.</div>
          ) : audit.map((a, i) => (
            <div key={a.id || i} className="flex items-baseline gap-2 px-1 py-0.5 hover:bg-zinc-800/50 rounded">
              <span className="text-[10px] text-zinc-500 font-mono w-32 shrink-0">
                {a.at ? new Date(a.at).toLocaleString("de-AT") : "—"}
              </span>
              <span className="text-blue-300 font-mono w-32 truncate">{a.user_email || "—"}</span>
              <span className="text-amber-300 font-mono w-20 truncate">{a.action || "—"}</span>
              <span className="text-zinc-400 font-mono truncate">{a.module || "—"}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-zinc-800 text-[10px] text-zinc-600 flex items-center justify-between">
        <span>Generiert: {data.generated_at ? new Date(data.generated_at).toLocaleString("de-AT") : "—"}</span>
        <span>Audit 24h: {fmtNum(data.activity?.audit_events_24h)} · Payroll-Exports 24h: {fmtNum(data.activity?.payroll_exports_24h)}</span>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent, testid }) {
  const accentCls = {
    emerald: "text-emerald-400",
    blue: "text-blue-400",
    rose: "text-rose-400",
    amber: "text-amber-400",
    zinc: "text-zinc-200",
  }[accent] || "text-zinc-200";
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`font-display font-semibold ${accentCls}`}>{value}</div>
    </div>
  );
}
